import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, HelpCircle, Pencil } from "lucide-react";
import { Input, Label } from "@/components/ui/Input";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { HoverHintTrigger } from "@/components/ui/title-help";
import { formatSkuMegaventoryCatalogDescription } from "@/lib/documents";
import { useTouchListPickSession } from "@/lib/useTouchListPickSession";
import { cn } from "@/lib/utils";
import { useHubCountrySelectOptions } from "@/lib/hubCountryOptions";
import {
  type ChargeLine,
  type SalesOrderChargeMode,
  formatMagnetCatalogDescription,
  isPlaceholderMagnetDescription,
} from "@integrator-src/sales-order/sales-order-charge-lines";
import {
  DEFAULT_SALES_ORDER_COUNTRY_CODE,
  normalizeSalesOrderCountryCode,
  normalizeSalesOrderRegionInput,
  SALES_ORDER_COUNTRY_OPTIONS,
  salesOrderRegionField,
} from "@integrator-src/sales-order/sales-order-address-country";
import { salesOrderEditControlGlyphKind } from "./salesOrderEditControlGlyph";
import type {
  PoViewerDocument,
  SalesOrderAttachment,
  SalesOrderEditLockState,
  SalesOrderItemDraft,
  SalesOrderPartyOption,
  SalesOrderSkuOption,
} from "./createSalesOrderPageModel";
import { DEFAULT_SHIPPING_SKU, EMPTY_ITEM } from "./createSalesOrderPageModel";


export function labelsForMagnetExemptions(exemptions: readonly string[]): string[] {
  const labels: string[] = [];
  if (exemptions.includes("mg_5a")) {
    labels.push("MG-5A");
  }
  if (exemptions.includes("mg_3c_2")) {
    labels.push("MG-3C-2");
  }
  return labels;
}
export const SALES_ORDER_COUNTRY_SELECT_OPTIONS: SearchableSelectOption[] = SALES_ORDER_COUNTRY_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
}));

let nextLineItemDraftId = 0;

export function createItemDraft(patch: Partial<Omit<SalesOrderItemDraft, "draftId">> = {}): SalesOrderItemDraft {
  nextLineItemDraftId += 1;
  return {
    draftId: `sales-order-line-${nextLineItemDraftId}`,
    ...EMPTY_ITEM,
    ...patch,
  };
}

export function isShippingItem(item: Pick<SalesOrderItemDraft, "sku">, configuredSku = DEFAULT_SHIPPING_SKU): boolean {
  const sku = item.sku.trim().toLowerCase();
  return sku === configuredSku.trim().toLowerCase() || sku === DEFAULT_SHIPPING_SKU.toLowerCase();
}

export function isSystemChargeItem(item: Pick<SalesOrderItemDraft, "origin">): boolean {
  return item.origin === "auto-shipping" || item.origin === "auto-magnet";
}

export function mergeChargedDrafts(
  current: SalesOrderItemDraft[],
  charged: ChargeLine[],
  skuCatalog: SalesOrderSkuOption[],
): SalesOrderItemDraft[] {
  const remaining = [...current];
  return charged.map((line) => {
    const matchIndex = remaining.findIndex(
      (item) =>
        item.origin === line.origin &&
        item.sku.trim().toUpperCase() === line.sku.trim().toUpperCase(),
    );
    if (matchIndex >= 0) {
      const existing = remaining.splice(matchIndex, 1)[0];
      if (line.origin === "user") {
        return existing;
      }
      return {
        ...existing,
        sku: line.sku,
        origin: line.origin,
        quantity:
          line.origin === "auto-magnet"
            ? String(line.quantity)
            : existing.quantity.trim() !== ""
              ? existing.quantity
              : String(line.quantity),
        unitPrice: existing.unitPrice.trim() !== "" ? existing.unitPrice : formatLineItemUnitPriceField(line.unitPrice),
        productDescription:
          line.origin === "auto-magnet" && isPlaceholderMagnetDescription(existing.productDescription, line.sku)
            ? line.productDescription || existing.productDescription
            : existing.productDescription || line.productDescription || "",
      };
    }
    const catalog = skuCatalog.find((option) => option.sku.trim().toUpperCase() === line.sku.trim().toUpperCase());
    return createItemDraft({
      sku: line.sku,
      templateId: catalog?.templateId ?? null,
      productDescription:
        line.productDescription ??
        (formatMagnetCatalogDescription(catalog?.description, catalog?.variant) || line.sku),
      quantity: String(line.quantity),
      unitPrice: formatLineItemUnitPriceField(line.unitPrice),
      megaventoryId: catalog?.megaventoryId ?? "",
      origin: line.origin,
    });
  });
}

export function chargeDraftsMatch(left: SalesOrderItemDraft[], right: SalesOrderItemDraft[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((item, index) => {
    const other = right[index];
    return (
      item.draftId === other.draftId &&
      item.origin === other.origin &&
      item.sku === other.sku &&
      item.quantity === other.quantity &&
      item.unitPrice === other.unitPrice &&
      Boolean(item.waivedPreview) === Boolean(other.waivedPreview)
    );
  });
}

export function serializeSalesOrderEditorSnapshot(input: {
  shipByDate: string;
  customerPurchaseOrderNumber: string;
  paymentTerms: string;
  clientFsAccountId: string | null;
  endUserFsAccountId: string | null;
  shipToRecipient: string;
  shipToAttention: string;
  shipToAddressLine1: string;
  shipToAddressLine2: string;
  shipToCity: string;
  shipToState: string;
  shipToZip: string;
  shipToCountry: string;
  billToRecipient: string;
  billToLine1: string;
  billToLine2: string;
  billToLine3: string;
  billToCity: string;
  billToState: string;
  billToZip: string;
  billToCountry: string;
  pilotProgram: boolean;
  specialInstructions: string;
  shippingChargeMode: SalesOrderChargeMode;
  magnetChargeMode: SalesOrderChargeMode;
  trackingRecipientEmails: string[];
  invoiceRecipientEmails: string[];
  items: Array<{
    sku: string;
    productDescription: string;
    quantity: string;
    unitPrice: string;
    remark: string;
    origin: ChargeLine["origin"];
  }>;
  attachments: string[];
}): string {
  return JSON.stringify(input);
}

export function keepShippingItemsLast(items: SalesOrderItemDraft[], configuredSku = DEFAULT_SHIPPING_SKU): SalesOrderItemDraft[] {
  return [
    ...items.filter((item) => !isShippingItem(item, configuredSku)),
    ...items.filter((item) => isShippingItem(item, configuredSku)),
  ];
}

export function normalizeLookupText(value: string): string {
  return value.toLowerCase().replace(/[-_\s]/g, "");
}

export function rankLookup(label: string, fsAccountId: string | null, search: string): number {
  const lowered = search.toLowerCase();
  const normalized = normalizeLookupText(search);
  const labelLower = label.toLowerCase();
  const labelNormalized = normalizeLookupText(label);
  if (labelLower === lowered) return 0;
  if (labelNormalized === normalized && normalized !== "") return 1;
  if (labelLower.startsWith(lowered)) return 2;
  if (labelNormalized.startsWith(normalized) && normalized !== "") return 3;
  if (fsAccountId != null && fsAccountId.includes(search)) return 4;
  if (labelLower.includes(lowered)) return 5;
  if (labelNormalized.includes(normalized) && normalized !== "") return 6;
  return 7;
}

export function filterPartyCatalog(options: SalesOrderPartyOption[], search: string): SalesOrderPartyOption[] {
  const trimmed = search.trim();
  const lowered = trimmed.toLowerCase();
  const normalized = normalizeLookupText(trimmed);
  const matches = options.filter((option) => {
    if (trimmed === "") return true;
    const labelLower = option.label.toLowerCase();
    const labelNormalized = normalizeLookupText(option.label);
    return (
      labelLower.includes(lowered) ||
      (normalized !== "" && labelNormalized.includes(normalized)) ||
      (option.fsAccountId != null && option.fsAccountId.includes(trimmed))
    );
  });
  return matches
    .sort((left, right) => {
      const diff = rankLookup(left.label, left.fsAccountId, trimmed) - rankLookup(right.label, right.fsAccountId, trimmed);
      if (diff !== 0) return diff;
      return left.label.localeCompare(right.label);
    })
    .slice(0, 12);
}

function skuCatalogDisplayText(option: SalesOrderSkuOption): string {
  return formatSkuMegaventoryCatalogDescription(option.description, option.variant);
}

export function rankSku(option: SalesOrderSkuOption, search: string): number {
  const lowered = search.toLowerCase();
  const normalized = normalizeLookupText(search);
  const skuLower = option.sku.toLowerCase();
  const skuNormalized = normalizeLookupText(option.sku);
  const catalogLower = skuCatalogDisplayText(option).toLowerCase();
  if (skuLower === lowered) return 0;
  if (skuNormalized === normalized && normalized !== "") return 1;
  if (skuLower.startsWith(lowered)) return 2;
  if (skuNormalized.startsWith(normalized) && normalized !== "") return 3;
  if (catalogLower.includes(lowered)) return 4;
  if (skuLower.includes(lowered)) return 5;
  if (normalized !== "" && skuNormalized.includes(normalized)) return 6;
  return 7;
}

export function filterSkuCatalog(options: SalesOrderSkuOption[], search: string): SalesOrderSkuOption[] {
  const trimmed = search.trim();
  const lowered = trimmed.toLowerCase();
  const normalized = normalizeLookupText(trimmed);
  const matches = options.filter((option) => {
    if (trimmed === "") return true;
    const skuLower = option.sku.toLowerCase();
    const skuNormalized = normalizeLookupText(option.sku);
    const catalogLower = skuCatalogDisplayText(option).toLowerCase();
    const variantLower = (option.variant || "").toLowerCase();
    return (
      skuLower.includes(lowered) ||
      catalogLower.includes(lowered) ||
      variantLower.includes(lowered) ||
      (normalized !== "" && skuNormalized.includes(normalized))
    );
  });
  return matches
    .sort((left, right) => {
      const diff = rankSku(left, trimmed) - rankSku(right, trimmed);
      if (diff !== 0) return diff;
      return left.sku.localeCompare(right.sku);
    })
    .slice(0, 12);
}

export function stateFromPartyLabel(label: string | null | undefined): string | null {
  const trimmed = (label ?? "").trim();
  if (trimmed === "") return null;
  const match = trimmed.match(/(?:^|\s)(?:-\s*)?\(([A-Z]{2})\)\s*$/i);
  return match ? match[1].toUpperCase() : null;
}

export function partyStateDisplay(option: SalesOrderPartyOption): string {
  const state = option.state?.trim() || stateFromPartyLabel(option.label);
  return state ? `State ${state}` : "No state listed";
}

export function PartyLookupField({
  id,
  label,
  kind,
  value,
  onSelect,
  error,
  catalog,
  isLoading,
}: {
  id: string;
  label: string;
  kind: "client" | "end-user";
  value: SalesOrderPartyOption | null;
  onSelect: (option: SalesOrderPartyOption | null) => void;
  error?: string | null;
  catalog: SalesOrderPartyOption[];
  isLoading: boolean;
}) {
  const [draft, setDraft] = useState(value?.label ?? "");
  const [isOpen, setIsOpen] = useState(false);
  const valueRef = useRef(value);
  valueRef.current = value;
  const touchListPick = useTouchListPickSession(isOpen);

  useEffect(() => {
    setDraft(value?.label ?? "");
  }, [value?.label]);

  const options = useMemo(() => filterPartyCatalog(catalog, draft), [catalog, draft]);
  const showOptions = isOpen;

  const helperText = useMemo(() => {
    if (error) return error;
    if (draft.trim().length > 0 && !value) return `Select a ${kind === "client" ? "client" : "end user"} from the list.`;
    return null;
  }, [draft, error, kind, value]);

  return (
    <div className="relative">
      <div className="flex h-8 items-center">
        <Label htmlFor={id}>{label}</Label>
      </div>
      <Input
        id={id}
        value={draft}
        autoComplete="off"
        onFocus={() => setIsOpen(true)}
        onChange={(event) => {
          setDraft(event.target.value);
          onSelect(null);
          setIsOpen(true);
        }}
        onBlur={() => {
          window.setTimeout(() => {
            if (touchListPick.shouldYieldBlurCommit()) return;
            setIsOpen(false);
            if (valueRef.current == null) {
              setDraft("");
            }
          }, 120);
        }}
        placeholder={kind === "client" ? "Search clients" : "Search end users"}
        className="mt-1"
      />
      {showOptions ? (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {isLoading ? (
            <div className="px-3 py-2 text-sm text-slate-500">Loading…</div>
          ) : null}
          {!isLoading && options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">No matches found.</div>
          ) : null}
          {options.map((option) => (
            <button
              key={`${kind}-${option.fsAccountId ?? option.label}`}
              type="button"
              className="block w-full border-b border-slate-100 px-3 py-2 text-left last:border-b-0 hover:bg-slate-50"
              onPointerDown={(event) => {
                touchListPick.onListItemPointerDown(event, () => {
                  onSelect(option);
                  setDraft(option.label);
                  setIsOpen(false);
                });
              }}
            >
              <div className="text-sm font-medium text-slate-900">{option.label}</div>
              <div className="text-xs text-slate-500">{partyStateDisplay(option)}</div>
            </button>
          ))}
        </div>
      ) : null}
      {helperText ? <p className={`mt-1 text-xs ${error ? "text-red-600" : "text-slate-500"}`}>{helperText}</p> : null}
    </div>
  );
}

export function AddressRegionPicker({
  id,
  country,
  value,
  onChange,
  disabled = false,
}: {
  id: string;
  country: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const field = salesOrderRegionField(country);
  const [searchValue, setSearchValue] = useState("");
  const normalized = normalizeSalesOrderRegionInput(country, value);
  const selectOptions = useMemo(() => {
    const options = field.entries.map((entry) => ({
      value: entry.value,
      label: entry.value,
      description: entry.name,
    }));
    if (normalized !== "" && !options.some((option) => option.value === normalized)) {
      return [
        { value: normalized, label: value.trim() || normalized, description: "Stored value" },
        ...options,
      ];
    }
    return options;
  }, [field.entries, normalized, value]);

  if (field.kind === "text") {
    return (
      <Input
        id={id}
        value={value}
        disabled={disabled}
        placeholder={field.regionPlaceholder}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  return (
    <SearchableSelect
      id={id}
      value={normalized}
      onValueChange={(option) => onChange(option?.value ?? "")}
      options={selectOptions}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      placeholder={field.regionPlaceholder}
      searchPlaceholder={`Search ${field.regionLabel.toLowerCase()}`}
      emptyMessage={`No matching ${field.regionLabel.toLowerCase()}.`}
      selectedLabelOverride={normalized || null}
      panelMinWidth={240}
      disabled={disabled}
    />
  );
}

export function AddressCountryPicker({
  id,
  value,
  onChange,
  disabled = false,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const [searchValue, setSearchValue] = useState("");
  const hubCountryOptions = useHubCountrySelectOptions();
  const normalized = normalizeSalesOrderCountryCode(value);
  const options = useMemo(() => {
    if (normalized !== "" && !hubCountryOptions.some((option) => option.value === normalized)) {
      return [
        { value: normalized, label: value.trim() || normalized, description: "Stored value" },
        ...hubCountryOptions,
      ];
    }
    return hubCountryOptions;
  }, [hubCountryOptions, normalized, value]);

  return (
    <SearchableSelect
      id={id}
      value={normalized}
      onValueChange={(option) => onChange(option?.value ?? DEFAULT_SALES_ORDER_COUNTRY_CODE)}
      options={options}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      placeholder="Select country"
      searchPlaceholder="Search country"
      emptyMessage="No matching country."
      selectedLabelOverride={
        hubCountryOptions.find((option) => option.value === normalized)?.label ?? (normalized || null)
      }
      panelMinWidth={240}
      disabled={disabled}
    />
  );
}

export function skuOptionToSelectOption(option: SalesOrderSkuOption): SearchableSelectOption {
  const description = skuCatalogDisplayText(option);
  return {
    value: option.sku,
    label: option.sku,
    description: description || "No description",
  };
}

export function SkuLookupField({
  id,
  value,
  onSelectOption,
  catalog,
  isLoading,
  disabled,
}: {
  id: string;
  value: SalesOrderItemDraft;
  onSelectOption: (option: SalesOrderSkuOption) => void;
  catalog: SalesOrderSkuOption[];
  isLoading: boolean;
  disabled?: boolean;
}) {
  const [searchValue, setSearchValue] = useState("");
  const options = useMemo(() => {
    const filtered = filterSkuCatalog(catalog, searchValue).map(skuOptionToSelectOption);
    if (value.sku.trim() !== "" && !filtered.some((option) => option.value === value.sku)) {
      const selected = catalog.find((option) => option.sku === value.sku);
      return [
        {
          value: value.sku,
          label: value.sku,
          description:
            (selected ? skuCatalogDisplayText(selected) : "") ||
            value.productDescription ||
            "No description",
        },
        ...filtered,
      ];
    }
    return filtered;
  }, [catalog, searchValue, value.productDescription, value.sku]);

  return (
    <div>
      <Label htmlFor={id} className="sr-only">
        SKU
      </Label>
      <SearchableSelect
        id={id}
        value={value.sku}
        onValueChange={(option) => {
          if (!option) {
            onSelectOption({
              sku: "",
              description: "",
              variant: "",
              megaventoryId: "",
              templateId: null,
            });
            return;
          }
          const catalogOption = catalog.find((entry) => entry.sku === option.value);
          onSelectOption(
            catalogOption ?? {
              sku: option.value,
              description: option.description ?? "",
              variant: "",
              megaventoryId: "",
              templateId: null,
            },
          );
        }}
        options={options}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        placeholder="Search SKU"
        searchPlaceholder="Search SKU"
        emptyMessage="No matches found."
        isLoading={isLoading}
        disabled={disabled}
        selectedLabelOverride={value.sku || null}
        panelMinWidth={320}
      />
    </div>
  );
}

export function freshdeskTicketIdFromLink(link: string | null | undefined): number | null {
  if (!link) return null;
  const match = /\/tickets\/(\d+)/i.exec(link.trim());
  if (!match) return null;
  const ticketId = Number.parseInt(match[1], 10);
  return Number.isFinite(ticketId) && ticketId > 0 ? ticketId : null;
}

export function isPdfPoDocument(poDocument: PoViewerDocument | null): boolean {
  if (!poDocument) return false;
  return poDocument.mimeType === "application/pdf" || /\.pdf$/i.test(poDocument.fileName);
}

export function isImagePoDocument(poDocument: PoViewerDocument | null): boolean {
  if (!poDocument) return false;
  return Boolean(poDocument.mimeType?.startsWith("image/")) || /\.(png|jpe?g|webp|gif)$/i.test(poDocument.fileName);
}

export function isAnalyzablePoDocument(poDocument: PoViewerDocument | null): boolean {
  if (!poDocument || poDocument.textBody != null) return false;
  return isPdfPoDocument(poDocument) || isImagePoDocument(poDocument);
}

export function serializeSalesOrderAttachments(attachments: SalesOrderAttachment[]): SalesOrderAttachment[] {
  return attachments
    .filter((attachment) => attachment.localFile == null)
    .map(({ localUrl: _localUrl, localFile: _localFile, ...attachment }) => attachment);
}

export function formatAddressPreview(address: {
  recipient: string | null;
  line1: string | null;
  line2: string | null;
  line3?: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  attention?: string | null;
}): string[] {
  const lines = [
    address.recipient,
    address.attention,
    address.line1,
    address.line2,
    address.line3 ?? null,
    [address.city, address.state, address.zip].filter(Boolean).join(", ").replace(/, $/, ""),
    address.country,
  ]
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter((value) => {
      if (value === "") return false;
      const normalized = value.toLowerCase().replace(/[^a-z0-9]/g, "");
      return normalized !== "none" && normalized !== "na" && normalized !== "null";
    });

  return lines;
}

export function normalizeDateInputValue(value: string | null | undefined): string {
  if (!value) return "";
  return value.slice(0, 10);
}

export function formatDateUs(value: string | null | undefined): string {
  const normalized = normalizeDateInputValue(value);
  if (!normalized) return "";
  const [year, month, day] = normalized.split("-");
  if (!year || !month || !day) return normalized;
  return `${month}/${day}/${year}`;
}

export function formatLineItemUnitPriceField(value: string | number | null | undefined): string {
  if (value == null) {
    return "";
  }
  const trimmed = String(value).trim();
  if (trimmed === "") {
    return "";
  }
  const parsed = Number.parseFloat(trimmed);
  if (!Number.isFinite(parsed)) {
    return trimmed;
  }
  return parsed.toFixed(2);
}

export const lineItemNumericInputClass = "text-right tabular-nums";

export function todayDateOnly(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}


export function formatAddressHoverText(address: {
  recipient: string | null;
  attention?: string | null;
  line1: string | null;
  line2: string | null;
  line3?: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
} | null): string {
  if (!address) return "No previous address available.";
  const lines = formatAddressPreview(address);
  return lines.length > 0 ? lines.join("\n") : "No previous address available.";
}

export function normalizeAddressPreviewLine(line: string): string {
  const trimmed = line.trim();
  if (trimmed === "") return "";
  const normalized = trimmed.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (normalized === "" || normalized === "none" || normalized === "na" || normalized === "null") {
    return "";
  }
  return normalized;
}

export function normalizeAddressLines(lines: string[]): string[] {
  return lines.map(normalizeAddressPreviewLine).filter((line) => line !== "");
}

export function addressesMatch(
  left: {
    recipient: string | null;
    line1: string | null;
    line2: string | null;
    line3?: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
    attention?: string | null;
  } | null,
  right: {
    recipient: string | null;
    line1: string | null;
    line2: string | null;
    line3?: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
    attention?: string | null;
  } | null,
): boolean {
  if (!left || !right) return false;
  const leftLines = normalizeAddressLines(formatAddressPreview(left));
  const rightLines = normalizeAddressLines(formatAddressPreview(right));
  if (leftLines.length === 0 || rightLines.length === 0) return false;
  return JSON.stringify(leftLines) === JSON.stringify(rightLines);
}

export function trimTrailingWhitespace(value: string): string {
  return value.replace(/\s+$/g, "");
}

export function isInvalidLineItemQuantity(value: string): boolean {
  const parsed = Number.parseFloat(value.trim());
  return !Number.isFinite(parsed) || parsed < 1;
}

export const lineItemExpandableInputClass =
  "transition-[width,box-shadow] duration-150 ease-out focus:relative focus:z-20 focus:w-[160%] focus:min-w-[16rem] focus:max-w-[32rem] focus:shadow-lg";

export function normalizeAddressFieldValue(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

export type AddressFieldComparison = {
  tone: "none" | "match" | "diff";
  title: string;
};

export function compareAddressField(
  currentValue: string,
  previousValue: string | null | undefined,
  previousAddress: {
    recipient: string | null;
    line1: string | null;
    line2: string | null;
    line3?: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
    attention?: string | null;
  } | null,
  previousAddressLabel: string,
): AddressFieldComparison {
  if (!previousAddress) {
    return {
      tone: "none",
      title: `No previous ${previousAddressLabel} available.`,
    };
  }

  const previousRaw = (previousValue ?? "").trim();
  if (previousRaw === "") {
    return {
      tone: "none",
      title: `No previous value saved for this field from the ${previousAddressLabel}.\n\nPrevious ${previousAddressLabel}:\n${formatAddressHoverText(previousAddress)}`,
    };
  }

  const matches = normalizeAddressFieldValue(currentValue) === normalizeAddressFieldValue(previousRaw);
  if (matches) {
    return {
      tone: "match",
      title: `Matches previous ${previousAddressLabel}.\nPrevious value: ${previousRaw}`,
    };
  }

  return {
    tone: "diff",
    title: `Does not match previous ${previousAddressLabel}.\nPrevious value: ${previousRaw}\n\nPrevious ${previousAddressLabel}:\n${formatAddressHoverText(previousAddress)}`,
  };
}

export function AddressFieldIndicator({
  comparison,
  onApply,
}: {
  comparison: AddressFieldComparison;
  onApply?: () => void;
}) {
  if (comparison.tone === "match") {
    return (
      <span title={comparison.title} className="inline-flex items-center">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
      </span>
    );
  }
  if (comparison.tone === "diff") {
    if (!onApply) {
      return (
        <span title={comparison.title} className="inline-flex items-center">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" aria-hidden="true" />
        </span>
      );
    }
    return (
      <button
        type="button"
        title={comparison.title}
        onClick={onApply}
        className="inline-flex items-center justify-center rounded-sm text-amber-500 transition-colors hover:text-amber-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
      >
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    );
  }
  return (
    <span title={comparison.title} className="inline-flex items-center">
      <HelpCircle className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
    </span>
  );
}

export function AddressFieldLabel({
  htmlFor,
  label,
  comparison,
  onApply,
}: {
  htmlFor: string;
  label: string;
  comparison: AddressFieldComparison;
  onApply?: () => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={htmlFor} className="m-0">
        {label}
      </Label>
      <AddressFieldIndicator comparison={comparison} onApply={onApply} />
    </div>
  );
}

export function normalizedSalesOrderStatus(status: string): string {
  return status.trim().toLowerCase();
}

export function salesOrderStatusAppearance(status: string) {
  switch (normalizedSalesOrderStatus(status)) {
    case "pending":
      return {
        container: "border-amber-300 bg-amber-50",
        dot: "bg-amber-500",
        label: "text-amber-700",
        value: "text-amber-950",
      };
    case "verified":
    case "approved":
      return {
        container: "border-emerald-300 bg-emerald-50",
        dot: "bg-emerald-500",
        label: "text-emerald-700",
        value: "text-emerald-950",
      };
    case "cancelled":
    case "canceled":
      return {
        container: "border-red-300 bg-red-50",
        dot: "bg-red-500",
        label: "text-red-700",
        value: "text-red-950",
      };
    case "draft":
      return {
        container: "border-blue-300 bg-blue-50",
        dot: "bg-blue-500",
        label: "text-blue-700",
        value: "text-blue-950",
      };
    default:
      return {
        container: "border-slate-300 bg-slate-50",
        dot: "bg-slate-400",
        label: "text-slate-500",
        value: "text-slate-900",
      };
  }
}

const EDIT_CONTROL_GLYPH_BUTTON_CLASS =
  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1";

export function SalesOrderEditControlGlyph({
  isPendingOrder,
  isApproving,
  canEditOrder,
  editLock,
  isChecking,
  moduleWritesAllowed,
  onTakeOver,
}: {
  isPendingOrder: boolean;
  isApproving: boolean;
  canEditOrder: boolean;
  editLock: SalesOrderEditLockState | null;
  isChecking: boolean;
  moduleWritesAllowed: boolean;
  onTakeOver: () => void;
}): JSX.Element | null {
  const kind = salesOrderEditControlGlyphKind({
    isPendingOrder,
    isApproving,
    canEditOrder,
    heldByOther: editLock?.status === "held" && editLock.holder != null,
    isChecking,
    moduleWritesAllowed,
  });
  if (kind === "moduleReadOnly") {
    return (
      <HoverHintTrigger
        trigger={
          <span
            className={cn(EDIT_CONTROL_GLYPH_BUTTON_CLASS, "border-slate-200 bg-slate-50 text-slate-500")}
            aria-label="Inside Sales is read-only"
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </span>
        }
      >
        Inside Sales write access is required to edit sales orders.
      </HoverHintTrigger>
    );
  }
  if (kind === "approvedReadOnly") {
    return (
      <HoverHintTrigger
        trigger={
          <span
            className={cn(EDIT_CONTROL_GLYPH_BUTTON_CLASS, "border-red-200 bg-red-50 text-red-600")}
            aria-label="Document is read-only while approved"
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </span>
        }
      >
        Document is read-only while approved
      </HoverHintTrigger>
    );
  }
  if (kind === "owner") {
    return (
      <HoverHintTrigger
        trigger={
          <span
            className={cn(EDIT_CONTROL_GLYPH_BUTTON_CLASS, "border-emerald-200 bg-emerald-50 text-emerald-600")}
            aria-label="You control editing"
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </span>
        }
      >
        You control editing for this order. You can save changes while it is still Pending.
      </HoverHintTrigger>
    );
  }
  if (kind === "heldByOther") {
    const holderName = editLock?.holder?.displayName ?? "Another user";
    return (
      <HoverHintTrigger
        closeOnTriggerActivate
        trigger={
          <button
            type="button"
            className={cn(EDIT_CONTROL_GLYPH_BUTTON_CLASS, "border-red-200 bg-red-50 text-red-600 hover:bg-red-100")}
            aria-label={`${holderName} currently controls editing`}
            onClick={onTakeOver}
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </button>
        }
      >
        {`${holderName} currently controls editing. This page is read-only. Click to take over.`}
      </HoverHintTrigger>
    );
  }
  return (
    <HoverHintTrigger
      trigger={
        <span
          className={cn(EDIT_CONTROL_GLYPH_BUTTON_CLASS, "border-slate-200 bg-slate-50 text-slate-400")}
          aria-label={kind === "securing" ? "Securing editing access" : "Checking editing access"}
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
        </span>
      }
    >
      {kind === "securing" ? "Securing editing access for this Pending order." : "Checking editing access."}
    </HoverHintTrigger>
  );
}

